import React from "react";
import { Typography } from "@mui/material";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import Paper from "@components/Paper/Paper";
import CallAdditionalDocumentsCard from "@features/vessel-call/vessel-call-status/VesselCallDetails/CallAdditionalDocumentsCard";
import { useTranslation } from "react-i18next";
import type { MovementDocumentsSectionProps } from "./MovementDocumentsSection.types";

const LANG_BASE = "vessel-call.movement-services";
const ADDITIONAL_DOCUMENT_LABEL_KEY = `${LANG_BASE}.preview.documents.additional-document`;
const MovementDocumentsSection: React.FC<MovementDocumentsSectionProps> = ({
  documents,
  titleKey,
  title,
  testId,
  onDownload,
  onPreview,
  hideWrapper = false,
}) => {
  const { t } = useTranslation();

  if (!documents?.length) {
    return null;
  }

  const heading = titleKey ? t(titleKey) : title;
  const normalizeDocumentDate = (value?: string | Date | null) => {
    if (!value) return undefined;
    return value instanceof Date ? value.toISOString() : value;
  };

  const content = (
    <div className="grid gap-4 md:grid-cols-2">
      {documents
        .filter((doc) => doc.name !== ADDITIONAL_DOCUMENT_LABEL_KEY)
        .map((doc) => (
          <DocumentCard
            key={doc.id}
            title={t(doc.name)}
            fileName={doc.fileName}
            expiryDate={doc.expiryDate ?? undefined}
            expiryDateTrans={t(`${LANG_BASE}.additional-documents.expiry-date`)}
            documentTypeTrans={t(`${LANG_BASE}.additional-documents.document-type`)}
            documentType={doc.documentType}
            additionalFields={{
              ...(doc.additionalDocumentName
                ? {
                    [t(`${LANG_BASE}.additional-documents.document-name`)]:
                      doc.additionalDocumentName,
                  }
                : {}),
              ...(doc.issueDate
                ? { [t(`${LANG_BASE}.additional-documents.issue-date`)]: doc.issueDate }
                : {}),
              ...(doc.specify
                ? { [t(`${LANG_BASE}.additional-documents.other-specify`)]: doc.specify }
                : {}),
              ...(doc.summary
                ? { [t(`${LANG_BASE}.additional-documents.document-summary`)]: doc.summary }
                : {}),
            }}
            onDownload={() => onDownload?.(doc.id)}
            onPreview={() => onPreview?.(doc.id)}
          />
        ))}
      {documents
        .filter((doc) => doc.name === ADDITIONAL_DOCUMENT_LABEL_KEY)
        .map((doc, idx) => (
          <div key={`${doc.name}-${idx}`} className="w-full h-full [&>*]:h-full">
            <CallAdditionalDocumentsCard
              testId={`Shipping-agency-transfer--additional-document-${idx}`}
              onDownload={() => onDownload?.(doc.id)}
              onPreview={() => onPreview?.(doc.id)}
              fileName={doc.fileName}
              issueDate={normalizeDocumentDate(doc.issueDate)}
              expiryDate={normalizeDocumentDate(doc.expiryDate)}
              documentType={doc.documentType}
              otherSpecifyType={doc.specify}
              documentSummary={doc.summary}
              documentName={doc.additionalDocumentName}
            />
          </div>
        ))}
    </div>
  );

  if (hideWrapper) {
    return (
      <div data-testid={testId} className="flex flex-col gap-4">
        {heading ? (
          <Typography variant="subtitle1" className="text-[#111827]">
            {heading}
          </Typography>
        ) : null}
        {content}
      </div>
    );
  }

  return (
    <Paper variant="outlined" data-testid={testId}>
      <div className="flex flex-col gap-4">
        {heading ? (
          <Typography variant="subtitle1" className="text-[#111827]">
            {heading}
          </Typography>
        ) : null}
        {content}
      </div>
    </Paper>
  );
};

export default MovementDocumentsSection;
